import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { figmaColorScales, figmaSemanticColors } from '../../../core/tokens';
import { kaiTypographyWeb, pxToRem } from '../../typography';
import { LogoutIcon, SupportIcon, UserProfileIcon } from '../Icons';
import type { AdminChromeProfileMenuProps } from './types';

type MenuItemVisualState =
  | 'default'
  | 'hover'
  | 'pressed'
  | 'selected'
  | 'selectedHover'
  | 'selectedPressed';

const PROFILE_MENU_COLORS = {
  surface: figmaColorScales.neutral[900],
  summary: figmaColorScales.neutral[800],
  hoverBackground: figmaColorScales.neutral[800],
  pressedBackground: figmaColorScales.neutral[700],
  selectedPressedBackground: '#000000',
  text: figmaSemanticColors.text.inverse,
  muted: figmaColorScales.neutral[400],
  hoverText: figmaColorScales.neutral[50],
  secondary: figmaColorScales.neutral[200],
  active: figmaColorScales.yellow[500],
} as const;

const MENU_ITEM_STYLES = {
  default: {
    background: PROFILE_MENU_COLORS.surface,
    color: PROFILE_MENU_COLORS.muted,
    text: {
      ...kaiTypographyWeb.small,
      lineHeight: 1.393, // matches small's font-size (19.5px / 14px)
      letterSpacing: '-0.0762px',
    },
  },
  hover: {
    background: PROFILE_MENU_COLORS.hoverBackground,
    color: PROFILE_MENU_COLORS.hoverText,
    text: {
      ...kaiTypographyWeb.small,
      lineHeight: 1.143, // matches small's font-size (16px / 14px)
      letterSpacing: '0.28px',
    },
  },
  pressed: {
    background: PROFILE_MENU_COLORS.pressedBackground,
    color: PROFILE_MENU_COLORS.hoverText,
    text: {
      ...kaiTypographyWeb.smallBold,
      lineHeight: 1.143, // matches smallBold's font-size (16px / 14px)
      letterSpacing: '0.28px',
    },
  },
  selected: {
    background: PROFILE_MENU_COLORS.surface,
    color: PROFILE_MENU_COLORS.active,
    text: {
      ...kaiTypographyWeb.smallBold,
      lineHeight: 1.393, // matches smallBold's font-size (19.5px / 14px)
      letterSpacing: '-0.0762px',
    },
  },
  selectedHover: {
    background: PROFILE_MENU_COLORS.hoverBackground,
    color: PROFILE_MENU_COLORS.active,
    text: {
      ...kaiTypographyWeb.smallBold,
      lineHeight: 1.393, // matches smallBold's font-size (19.5px / 14px)
      letterSpacing: '-0.0762px',
    },
  },
  selectedPressed: {
    background: PROFILE_MENU_COLORS.selectedPressedBackground,
    color: PROFILE_MENU_COLORS.active,
    text: {
      ...kaiTypographyWeb.smallBold,
      lineHeight: 1.393, // matches smallBold's font-size (19.5px / 14px)
      letterSpacing: '-0.0762px',
    },
  },
} as const satisfies Record<
  MenuItemVisualState,
  { background: string; color: string; text: CSSProperties }
>;

const TRIGGER_WRAPPER_STYLE: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const TRIGGER_BUTTON_STYLE: CSSProperties = {
  width: 32,
  height: 32,
  minWidth: 32,
  border: 0,
  borderRadius: 999,
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: PROFILE_MENU_COLORS.muted,
  color: PROFILE_MENU_COLORS.surface,
  cursor: 'pointer',
};

const TRIGGER_INITIALS_STYLE: CSSProperties = {
  ...kaiTypographyWeb.xxSmallBold,
  lineHeight: 1.5, // matches xxSmallBold's font-size (15px / 10px)
  letterSpacing: '0px',
};

const MENU_STYLE: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 5px)',
  right: 0,
  width: 240,
  border: `1px solid ${PROFILE_MENU_COLORS.surface}`,
  borderRadius: 5,
  background: PROFILE_MENU_COLORS.surface,
  boxShadow: '0 4px 3px rgba(29, 29, 29, 0.1), 0 10px 7.5px rgba(29, 29, 29, 0.1)',
  boxSizing: 'border-box',
  overflow: 'hidden',
  zIndex: 1205,
};

const SUMMARY_STYLE: CSSProperties = {
  padding: 12,
  borderBottom: 'none',
  borderRadius: '5px 5px 0 0',
  background: PROFILE_MENU_COLORS.summary,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  gap: 1,
};

const NAME_STYLE: CSSProperties = {
  ...kaiTypographyWeb.mediumBold,
  lineHeight: 1.313, // matches mediumBold's font-size (21px / 16px)
  letterSpacing: '-0.1504px',
  color: PROFILE_MENU_COLORS.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const ORG_NAME_STYLE: CSSProperties = {
  ...kaiTypographyWeb.xSmall,
  lineHeight: 1.375, // matches xSmall's font-size (16.5px / 12px)
  letterSpacing: '0.0645px',
  color: PROFILE_MENU_COLORS.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const MENU_ITEM_ICON_STYLE: CSSProperties = {
  width: 24,
  height: 24,
  minWidth: 24,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const getMenuItemVisualState = (
  selected: boolean,
  isHovered: boolean,
  isPressed: boolean,
): MenuItemVisualState => {
  if (selected) {
    if (isPressed) return 'selectedPressed';
    if (isHovered) return 'selectedHover';
    return 'selected';
  }

  if (isPressed) return 'pressed';
  if (isHovered) return 'hover';
  return 'default';
};

const getMenuItemStyle = (visualState: MenuItemVisualState): CSSProperties => {
  const visualStyle = MENU_ITEM_STYLES[visualState];

  return {
    width: '100%',
    height: 40,
    border: 0,
    padding: '0 16px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    background: visualStyle.background,
    color: visualStyle.color,
    textAlign: 'left',
    cursor: 'pointer',
  };
};

const getMenuItemLabelStyle = (visualState: MenuItemVisualState): CSSProperties => ({
  ...MENU_ITEM_STYLES[visualState].text,
  color: 'currentColor',
});

const getInitials = (name: string) =>
  name
    .trim()
    .split(' ')
    .filter(Boolean)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

type MenuItemDefinition = {
  action: string;
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
};

export const AdminChromeProfileMenu = ({
  displayName,
  orgLabel,
  selectedAction,
  onProfileClick,
  onSupportClick,
  onLogoutClick,
  profileLabel = 'My Profile',
  supportLabel = 'Help & Support',
  logoutLabel = 'Log Out',
  extraItems = [],
}: AdminChromeProfileMenuProps) => {
  const [open, setOpen] = useState(false);
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);
  const [pressedAction, setPressedAction] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initials = useMemo(() => getInitials(displayName), [displayName]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: globalThis.MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const toggleMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen(value => !value);
    setHoveredAction(null);
    setPressedAction(null);
  }, []);

  const buildActionHandler = (handler?: () => void) => () => {
    setOpen(false);
    setHoveredAction(null);
    setPressedAction(null);
    handler?.();
  };

  const menuItems: MenuItemDefinition[] = [
    {
      action: 'profile',
      label: profileLabel,
      icon: <UserProfileIcon />,
      onClick: onProfileClick,
    },
    {
      action: 'support',
      label: supportLabel,
      icon: <SupportIcon />,
      onClick: onSupportClick,
    },
    ...extraItems,
    {
      action: 'logout',
      label: logoutLabel,
      icon: <LogoutIcon />,
      onClick: onLogoutClick,
    },
  ];

  return (
    <div ref={containerRef} style={TRIGGER_WRAPPER_STYLE}>
      <button
        type='button'
        style={TRIGGER_BUTTON_STYLE}
        aria-haspopup='menu'
        aria-expanded={open}
        title={displayName}
        onClick={toggleMenu}
      >
        <span style={TRIGGER_INITIALS_STYLE}>{initials}</span>
      </button>

      {open
        ? (
          <div style={MENU_STYLE} role='menu' aria-label='Profile menu'>
            <div style={SUMMARY_STYLE}>
              <div style={NAME_STYLE}>{displayName}</div>
              {orgLabel ? <div style={ORG_NAME_STYLE}>{orgLabel}</div> : null}
            </div>

            {menuItems.map(({ action, label, icon, onClick }) => {
              const selected = selectedAction === action;
              const visualState = getMenuItemVisualState(
                selected,
                hoveredAction === action,
                pressedAction === action,
              );

              return (
                <button
                  key={action}
                  type='button'
                  role='menuitem'
                  style={getMenuItemStyle(visualState)}
                  onClick={buildActionHandler(onClick)}
                  onMouseEnter={() => setHoveredAction(action)}
                  onMouseLeave={() => {
                    setHoveredAction(current => (current === action ? null : current));
                    setPressedAction(current => (current === action ? null : current));
                  }}
                  onMouseDown={() => setPressedAction(action)}
                  onMouseUp={() => setPressedAction(current => (current === action ? null : current))}
                  onBlur={() => {
                    setHoveredAction(current => (current === action ? null : current));
                    setPressedAction(current => (current === action ? null : current));
                  }}
                >
                  <span style={MENU_ITEM_ICON_STYLE}>{icon}</span>
                  <span style={getMenuItemLabelStyle(visualState)}>{label}</span>
                </button>
              );
            })}
          </div>
        )
        : null}
    </div>
  );
};
